import "./style.css";
import Api from "@/components/Api";

export default class PostList {
  static selector = ".post-list";

  constructor(block) {
    this.block = block;

    this.navButtons = this.block.querySelectorAll(".post-list__nav__button");
    for (const button of this.navButtons) {
      button.addEventListener("click", this.onTabChange);
    }

    this.contentPanels = this.block.querySelector(
      ".post-list__content"
    ).children;

    this.loadMoreButton = this.block.querySelector(".load-more");

    // wrapper that holds either the "no posts found" component or the posts container
    this.contentWrapper = this.block.querySelector(".post-list__content");

    // posts container
    this.postsList = this.block.querySelector(".post-list__content__items");

    this.noResults = this.block.querySelector("section no-results");

    if (this.postsList) {
      this.initPostsList();
    }

    this.currentTab = "upcoming";
  }

  initPostsList = () => {
    this.postType = this.block.dataset.postType;
    this.maxCount = parseInt(this.block.dataset.count, 10);
    this.pageSize = ( this.postsList.childElementCount < 7 ) ? 7 : this.postsList.childElementCount ;
  };

  addListeners = () => {
    this.loadMoreButton.addEventListener("click", this.handleLoadMoreClick);
  };

  appendPosts = (posts, tabChange = false) => {
    const itemTemplate = this.block.querySelector("template.article-card")
      .content;

    posts.forEach((postData, i) => {
      const { title, date, image_url, link_url, link_target } = postData;

      const itemElement = itemTemplate.cloneNode(true).firstElementChild;
      itemElement.classList.add("article-card--grid");

      // add featured class to first item in the list if we are loading new tab content (events page)
      // no need to do it if we are just loading more posts as we already have a featured one
      if (tabChange && i == 0) {
        itemElement.classList.add("article-card--featured");
      }

      const itemInner = itemElement.querySelector("a");

      itemInner.href = link_url;
      itemInner.setAttribute("target", link_target);

      itemElement.querySelector(
        ".article-card__image"
      ).style = `background-image: url('${image_url}')`;

      itemElement.querySelector(".article-card__text__date").textContent = date;
      itemElement.querySelector(
        ".article-card__text__title"
      ).textContent = title;

      this.postsList.appendChild(itemElement);

      this.count = this.postsList.childElementCount;

      if (this.count >= this.maxCount) {
        this.hideLoadMoreButton();
      }
    });
  };

  showNoPostsFound = () => {
    const noResultsTemplate = this.block.querySelector("template.no-results")
      .content;
    const noResultsElement = noResultsTemplate.cloneNode(true)
      .firstElementChild;

    const noResultsData = JSON.parse(this.block.dataset.noResults);
    noResultsElement.querySelector(".no-results__title").textContent =
      noResultsData.title;
    noResultsElement.querySelector(".no-results__subtitle").textContent =
      noResultsData.subtitle;
    noResultsElement
      .querySelector(".no-results__button")
      .setAttribute("onclick", "window.open('/', '_self')");
    noResultsElement.querySelector(".no-results__button").textContent =
      noResultsData.button;
    this.contentWrapper.appendChild(noResultsElement);
  };

  loadPosts = async (tabChange = false, tabId = null) => {
    try {
      const results = await Api.getPosts({
        page: this.page,
        size: this.pageSize,
        postType: this.postType,
        eventType: tabId,
      });

      const posts = results[0];
      this.maxCount = results[1] ? results[1] : this.maxCount;

      if (posts.length == 0) {
        this.hideLoadMoreButton();
        this.showNoPostsFound();
      }

      if (!tabChange) {
        // not a tab change so it's a load more
        this.page += 1;
        this.appendPosts(posts);
      } else {
        // it's a tab change so reset wrapper height and pass true to append featured post
        this.postsList.style.minHeight = "";
        this.appendPosts(posts, true);
      }
    } catch (error) {
      console.log("Error loading new posts: ", error);
    }
  };

  handleLoadMoreClick = async () => {
    this.loadMoreButton.classList.add("load-more--loading");
    this.loadMoreButton.disabled = true;

    this.page = this.page + 1;

    this.loadPosts(false, this.currentTab);

    this.loadMoreButton.disabled = false;
    this.loadMoreButton.classList.remove("load-more--loading");
  };

  createPostsContainer = () => {
    this.postsList = document.createElement("ul");
    this.postsList.classList.add("post-list__content__items");
    // set current height to avoid layout shift
    this.contentWrapperHeight = window.getComputedStyle(
      this.contentWrapper
    ).height;

    this.contentWrapper.style.minHeight = this.contentWrapperHeight;

    // remove children
    while (this.contentWrapper.firstChild) {
      this.contentWrapper.removeChild(this.contentWrapper.firstChild);
    }

    this.contentWrapper.appendChild(this.postsList);
    this.initPostsList();

    this.loadMoreButton = document.createElement("button");
    this.loadMoreButton.classList.add("button", "button--green", "load-more");
    this.loadMoreButton.innerText = this.block.dataset.loadMore;

    this.addListeners();

    this.contentWrapper.appendChild(this.loadMoreButton);
  };

  hideLoadMoreButton = () => {
    this.loadMoreButton.classList.add("load-more--done");
  };

  onTabChange = (e) => {
    const activeButton = e.currentTarget;
    this.currentTab = activeButton.dataset.id;

    for (const button of this.navButtons) {
      button.classList.toggle("active", false);
    }
    activeButton.classList.toggle("active", true);

    this.noResults = this.contentWrapper.querySelector(".no-results");
    if (this.noResults) {
      this.noResults.remove();
    }

    // set current height to avoid layout shift
    this.postsListHeight = window.getComputedStyle(this.postsList).height;
    this.postsList.style.minHeight = this.postsListHeight;

    /*
    if we land on a "no posts found" page and then we change tab,
    create posts container and load more button
    */
    if (!this.postsList) {
      this.createPostsContainer();
    } else {
      // reset page number
      this.page = 1;

      // reset load more button

      if (this.loadMoreButton) {
        this.loadMoreButton.classList.remove("load-more--done");
      }

      // remove all current children
      while (this.postsList.firstChild) {
        this.postsList.removeChild(this.postsList.firstChild);
      }
    }

    // passing true as we are changing tab (events page)
    this.loadPosts(true, this.currentTab);
  };

  onReady = () => {
    this.page = 1;

    if (this.postsList) {
      this.count = this.postsList.childElementCount;
    }

    if (this.loadMoreButton) {
      this.addListeners();
      if (this.count >= this.maxCount) {
        this.hideLoadMoreButton();
      }
    }
  };

  onDestroy = () => {
    if (this.loadMoreButton) {
      this.loadMoreButton.removeEventListener(
        "click",
        this.handleLoadMoreClick
      );
    }
  };
}
